import React from 'react';
import RACES from '../../data/races';
import STATS from '../../data/stats';
import SKILLS from '../../data/skills';
import STAR_SIGNS from '../../data/star_signs';
import MAGIC_SCHOOLS from '../../data/magic_schools';
import { getAllSkills } from '../../utils/helpers';
import { btnStyle } from '../../styles/theme';
import { STAT_COST, SKILL_COST, MAGIC_COST, ALCHEMY_COST, MAX_STAT, MAX_SKILL, MAX_MAGIC, MAX_ALCHEMY } from '../../config/gameConfig';
import SectionHeader from '../ui/SectionHeader';
import ExpPoolEditable from '../ui/ExpPoolEditable';
 import TieredAbilities from './TieredAbilities';

function LevelingView({ character, setCharacter, expPools, setExpPools }) {
  if (!character) return <div style={{ padding: 40, textAlign: "center", color: "#8a7e5e" }}>Loading character...</div>;

  const race = RACES.find(r => r.id === character.race);
  const starSign = STAR_SIGNS.find(s => s.id === character.starSign);
  const allSkills = getAllSkills();


  // Stat modifiers
  const modifiers = {
    might: Math.floor(character.stats.might / 10),
    speed: Math.floor(character.stats.speed / 10),
    intellect: Math.floor(character.stats.intellect / 10),
  };
  const magicPower = Math.floor(character.stats.magic / 5);

  function canAffordStat(statId) {
    const cost = STAT_COST;
    return (expPools.universal >= cost) || (expPools[statId] >= cost);
  }

  function upgradeStat(statId) {
    if (!canAffordStat(statId) || character.stats[statId] >= 50) return;
    const cost = STAT_COST;

    // Try universal first, then stat-specific
    if (expPools.universal >= cost) {
      setExpPools({ ...expPools, universal: expPools.universal - cost });
    } else if (expPools[statId] >= cost) {
      setExpPools({ ...expPools, [statId]: expPools[statId] - cost });
    }

    setCharacter({ ...character, stats: { ...character.stats, [statId]: character.stats[statId] + 1 } });
  }

  function canAffordSkill(skillId) {
    const skill = allSkills.find(s => s.id === skillId);
    if (!skill) return false;

    // Find which stat governs this skill
    let statId = null;
    if (SKILLS.might.find(s => s.id === skillId)) statId = "might";
    else if (SKILLS.speed.find(s => s.id === skillId)) statId = "speed";
    else if (SKILLS.intellect.find(s => s.id === skillId)) statId = "intellect";

    const cost = SKILL_COST;
    return (expPools.universal >= cost) || (statId && expPools[statId] >= cost);
  }

  function upgradeSkill(skillId) {
    const currentLevel = character.skills[skillId] || 0;
    if (!canAffordSkill(skillId) || currentLevel >= 10) return;

    // Find governing stat
    let statId = null;
    if (SKILLS.might.find(s => s.id === skillId)) statId = "might";
    else if (SKILLS.speed.find(s => s.id === skillId)) statId = "speed";
    else if (SKILLS.intellect.find(s => s.id === skillId)) statId = "intellect";

    const cost = SKILL_COST;

    // Try universal first, then stat-specific
    if (expPools.universal >= cost) {
      setExpPools({ ...expPools, universal: expPools.universal - cost });
    } else if (statId && expPools[statId] >= cost) {
      setExpPools({ ...expPools, [statId]: expPools[statId] - cost });
    }

    setCharacter({ ...character, skills: { ...character.skills, [skillId]: currentLevel + 1 } });
  }

  function canAffordAlchemy() {
    return expPools.universal >= ALCHEMY_COST || expPools.intellect >= ALCHEMY_COST;
  }

  function upgradeAlchemy(subset) {
    const currentClass = character.alchemy[subset] || 1;
    if (!canAffordAlchemy() || currentClass >= 5) return;

    const cost = ALCHEMY_COST;
    if (expPools.universal >= cost) {
      setExpPools({ ...expPools, universal: expPools.universal - cost });
    } else if (expPools.intellect >= cost) {
      setExpPools({ ...expPools, intellect: expPools.intellect - cost });
    }

    setCharacter({ ...character, alchemy: { ...character.alchemy, [subset]: currentClass + 1 } });
  }

  return (
    <div style={{ position: "relative", zIndex: 1, maxWidth: 820, margin: "0 auto", padding: "0 16px 40px" }}>
      {/* Character Header */}
      <div style={{ textAlign: "center", background: "linear-gradient(180deg, rgba(45,35,20,0.6), rgba(20,16,10,0.4))", border: "1px solid rgba(201,168,76,0.3)", borderRadius: "10px", padding: "16px", marginBottom: 20 }}>
        <div style={{ fontFamily: "'Cinzel Decorative', cursive", fontSize: "clamp(18px, 4vw, 26px)", color: "#c9a84c" }}>{character.name}</div>
        <div style={{ fontFamily: "'Cinzel', serif", fontSize: "12px", color: "#8a7e5e", marginTop: 4 }}>{race.name} · {starSign?.name}</div>
      </div>

      {/* EXP Pools */}
      <div style={{ background: "rgba(20,16,10,0.8)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: "8px", padding: "14px 18px", marginBottom: 20 }}>
        <div style={{ fontFamily: "'Cinzel', serif", fontSize: "12px", color: "#c9a84c", letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>Available Experience</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "12px" }}>
          <ExpPoolEditable label="Universal" amount={expPools.universal} color="#c9a84c"
            onChange={(val) => setExpPools({ ...expPools, universal: val })} />
          <ExpPoolEditable label="Might" amount={expPools.might} color="#c0392b"
            onChange={(val) => setExpPools({ ...expPools, might: val })} />
          <ExpPoolEditable label="Speed" amount={expPools.speed} color="#2980b9"
            onChange={(val) => setExpPools({ ...expPools, speed: val })} />
          <ExpPoolEditable label="Intellect" amount={expPools.intellect} color="#8e44ad"
            onChange={(val) => setExpPools({ ...expPools, intellect: val })} />
        </div>
      </div>

      {/* Stats */}
      <SectionHeader>Stats</SectionHeader>
      <div style={{ display: "grid", gap: "10px", marginBottom: 24 }}>
        {STATS.map(stat => {
          const current = character.stats[stat.id];
          const mod = stat.id !== "magic" ? modifiers[stat.id] : null;
          const atCap = current >= 50;
          const canAfford = canAffordStat(stat.id);
          return (
            <div key={stat.id} style={{ background: "rgba(20,16,10,0.6)", border: `1px solid ${stat.color}33`, borderRadius: "8px", padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: stat.color }} />
                <div>
                  <div style={{ fontFamily: "'Cinzel', serif", fontSize: "13px", color: stat.color }}>{stat.label}</div>
                  <div style={{ fontSize: "10px", color: "#6b6048" }}>{mod !== null ? `Modifier: +${mod}` : `Power: ${magicPower} dice`}</div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ fontFamily: "'Cinzel', serif", fontSize: "20px", color: stat.color }}>{current}</div>
                <button onClick={() => upgradeStat(stat.id)}
                  disabled={!canAfford || atCap}
                  style={{ ...btnStyle(canAfford && !atCap ? stat.color : "#4a4030", "transparent"), padding: "4px 10px", opacity: canAfford && !atCap ? 1 : 0.4, cursor: canAfford && !atCap ? "pointer" : "default" }}>
                  +1 ({STAT_COST} EXP)
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Skills */}
      <SectionHeader>Skills</SectionHeader>
      {["might","speed","intellect"].map(statId => {
        const stat = STATS.find(s => s.id === statId);
        const statMod = modifiers[statId];
        return (
          <div key={statId} style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: stat.color }} />
              <span style={{ fontFamily: "'Cinzel', serif", fontSize: "12px", color: stat.color }}>{stat.label} Skills</span>
              <span style={{ fontSize: "9px", color: "#6b6048" }}>(+{statMod} from stat)</span>
            </div>
            <div style={{ display: "grid", gap: 4 }}>
              {SKILLS[statId].map(skill => {
                const lvl = character.skills[skill.id] || 0;
                const atCap = lvl >= 10;
                const canAfford = canAffordSkill(skill.id);
                return (
                  <div key={skill.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(20,16,10,0.4)", borderRadius: 6, padding: "6px 10px", border: "1px solid rgba(201,168,76,0.08)" }}>
                    <div style={{ fontSize: "12px", color: "#c9a84c88" }}>{skill.name}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: "11px", color: "#8a7e5e" }}>Lvl {lvl}</span>
                      <button onClick={() => upgradeSkill(skill.id)}
                        disabled={!canAfford || atCap}
                        style={{ ...btnStyle(canAfford && !atCap ? "#c9a84c" : "#4a4030", "transparent"), padding: "2px 8px", fontSize: "10px", opacity: canAfford && !atCap ? 1 : 0.4, cursor: canAfford && !atCap ? "pointer" : "default" }}>
                        +1 ({SKILL_COST})
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Magic */}
      <SectionHeader>Magic Schools</SectionHeader>
      <p style={{ fontSize: "12px", color: "#8a7e5e", marginBottom: 14, lineHeight: 1.5 }}>
        Magic Schools cost <strong style={{ color: "#c9a84c" }}>4 EXP</strong> per level (max 10). Requires minimum Magic stat for each tier.
      </p>
      {["simple", "standard", "great", "master"].map(tier => {
        const schools = MAGIC_SCHOOLS[tier];
        const tierColor = tier === "simple" ? "#16a085" : tier === "standard" ? "#27ae60" : tier === "great" ? "#2980b9" : "#8e44ad";

        return (
          <div key={tier} style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: tierColor }} />
              <span style={{ fontFamily: "'Cinzel', serif", fontSize: "12px", color: tierColor, textTransform: "capitalize" }}>{tier} Tier</span>
              <span style={{ fontSize: "9px", color: "#6b6048" }}>
                (Min Magic: {schools[0].statMin} | Cost: {schools[0].useCost} Magic | Power: {schools[0].powerDice})
              </span>
            </div>
            <div style={{ display: "grid", gap: 4 }}>
              {schools.map(magic => {
                const lvl = character.magics?.[magic.id] || 0;
                const atCap = lvl >= 10;
                const meetsStatReq = character.stats.magic >= magic.statMin;
                const canAfford = (expPools.universal >= MAGIC_COST) || (expPools.intellect >= MAGIC_COST);
                const canUpgrade = canAfford && !atCap && meetsStatReq;

                return (
                  <div key={magic.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(20,16,10,0.4)", borderRadius: 6, padding: "6px 10px", border: `1px solid ${meetsStatReq ? "rgba(201,168,76,0.08)" : "rgba(192,57,43,0.2)"}` }}>
                    <div>
                      <span style={{ fontSize: "12px", color: meetsStatReq ? "#c9a84c88" : "#c0392b88" }}>{magic.name}</span>
                      {!meetsStatReq && <span style={{ fontSize: "9px", color: "#c0392b", marginLeft: 6 }}>(Requires {magic.statMin} Magic)</span>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: "11px", color: "#8a7e5e" }}>Lvl {lvl}</span>
                      <button onClick={() => {
                        if (!canUpgrade) return;
                        // Try universal first, then intellect
                        if (expPools.universal >= MAGIC_COST) {
                          setExpPools({ ...expPools, universal: expPools.universal - MAGIC_COST });
                        } else if (expPools.intellect >= MAGIC_COST) {
                          setExpPools({ ...expPools, intellect: expPools.intellect - MAGIC_COST });
                        }
                        setCharacter({ ...character, magics: { ...(character.magics || {}), [magic.id]: lvl + 1 } });
                      }}
                        disabled={!canUpgrade}
                        style={{ ...btnStyle(canUpgrade ? tierColor : "#4a4030", "transparent"), padding: "2px 8px", fontSize: "10px", opacity: canUpgrade ? 1 : 0.4, cursor: canUpgrade ? "pointer" : "default" }}>
                        +1 ({MAGIC_COST})
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Alchemy */}
      <SectionHeader>Alchemy</SectionHeader>
      <div style={{ display: "grid", gap: 8, marginBottom: 24 }}>
        {["auxiliary", "organic", "conversion", "augmentation"].map(subset => {
          const cls = character.alchemy[subset] || 1;
          const atCap = cls >= 5;
          const canAfford = canAffordAlchemy();
          return (
            <div key={subset} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(20,16,10,0.5)", border: "1px solid rgba(142,68,173,0.2)", borderRadius: 8, padding: "10px 14px" }}>
              <div>
                <div style={{ fontFamily: "'Cinzel', serif", fontSize: "13px", color: "#c39bd3", textTransform: "capitalize" }}>{subset}</div>
                <div style={{ fontSize: "10px", color: "#6b6048" }}>Uses Intellect EXP</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontFamily: "'Cinzel', serif", fontSize: "16px", color: "#8e44ad" }}>Class {cls}</span>
                <button onClick={() => upgradeAlchemy(subset)}
                  disabled={!canAfford || atCap}
                  style={{ ...btnStyle(canAfford && !atCap ? "#8e44ad" : "#4a4030", "transparent"), padding: "4px 10px", opacity: canAfford && !atCap ? 1 : 0.4, cursor: canAfford && !atCap ? "pointer" : "default" }}>
                  +1 ({ALCHEMY_COST} EXP)
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tiered Abilities */}
      <TieredAbilities
        character={character} 
        setCharacter={setCharacter}
        expPools={expPools}
        setExpPools={setExpPools}
      />


    </div>
  );
}





export default LevelingView
